/**
 * @engine/vfx — shared helpers for VFX preset builders.
 *
 * Extracted from VFXGraphAsset.ts so preset factories (in ./*.ts) and the
 * asset module can share id regeneration without a circular import.
 */

import type { VFXGraphData } from '../types';

/**
 * Helper to regenerate all IDs in a VFX graph template.
 * Creates a mapping of old IDs to new UUIDs and replaces them throughout.
 */
export function regenerateGraphIds<T extends VFXGraphData>(
  template: T,
  newName: string,
): T {
  const idMap = new Map<string, string>();
  const id = (oldId: string) => {
    if (!idMap.has(oldId)) {
      idMap.set(oldId, crypto.randomUUID());
    }
    return idMap.get(oldId)!;
  };

  return {
    ...template,
    id: crypto.randomUUID(),
    name: newName,
    nodes: template.nodes.map((n) => ({
      ...n,
      id: id(n.id),
      contextId: n.contextId ? id(n.contextId) : undefined,
    })),
    edges: template.edges.map((e) => ({
      ...e,
      id: id(e.id),
      from: { nodeId: id(e.from.nodeId), portId: e.from.portId },
      to: { nodeId: id(e.to.nodeId), portId: e.to.portId },
    })),
    contexts: template.contexts.map((c) => ({
      ...c,
      id: id(c.id),
      nodeIds: c.nodeIds.map((nid) => id(nid)),
    })),
    contextFlowEdges: (template.contextFlowEdges ?? []).map((e) => ({
      ...e,
      id: id(e.id),
      fromContextId: id(e.fromContextId),
      toContextId: id(e.toContextId),
    })),
    contextSettings: Object.fromEntries(
      Object.entries(template.contextSettings ?? {}).map(([k, v]) => [
        id(k),
        v,
      ]),
    ),
    metadata: {
      ...template.metadata,
      createdAt: new Date().toISOString(),
    },
  } as T;
}
